<template>
  <div class="ui-members">
    <b-table
        class="ui-members__table"
        responsive
        borderless
        hover
        show-empty
        stacked="md"
        :fields="fields"
        :items="teamMembers"
        empty-text="В команде нет игроков"
    >
      <!-- игрок: ник — ссылка на карточку, под ним копируемый id -->
      <template #cell(player)="{item}">
        <button type="button" class="ui-members__nick" @click="showUserInfo(item.id)"
                v-b-tooltip.hover.top="'Открыть карточку игрока'">
          {{ item.username || 'без ника' }}
          <i class="fa fa-external-link"></i>
        </button>
        <button type="button" class="ui-copy" @click="copyValue(item.id, 'ID игрока')"
                v-b-tooltip.hover.bottom="'Скопировать ID'">
          ID {{ item.id }} <i class="fa fa-copy"></i>
        </button>
      </template>

      <!-- игровой профиль: ник в игре и игровой id одной колонкой -->
      <template #cell(profile)="{item}">
        <template v-if="hasGameProfile(item)">
          <div class="ui-members__game-nick">{{ profileNick(item) || '—' }}</div>
          <button v-if="profileId(item)" type="button" class="ui-copy"
                  @click="copyValue(profileId(item), idLabel)"
                  v-b-tooltip.hover.bottom="'Скопировать ' + idLabel">
            {{ profileId(item) }} <i class="fa fa-copy"></i>
          </button>
        </template>
        <span v-else class="ui-muted">нет игрового профиля</span>
      </template>

      <template #cell(status)="{item}">
        <div class="ui-chips">
          <span class="tf-badge" :class="hasGameProfile(item) ? 'tf-badge--primary' : ''">
            {{ hasGameProfile(item) ? 'Игровой' : 'Не игровой' }}
          </span>
          <span class="tf-badge" :class="isActivated(item) ? 'tf-badge--ok' : 'tf-badge--warn'">
            {{ isActivated(item) ? 'Активирован' : 'Не активирован' }}
          </span>
          <span v-if="isBanned(item)" class="tf-badge tf-badge--danger">
            <i class="fa fa-ban"></i> В бане
          </span>
        </div>
      </template>

      <template #cell(country)="{item}">
        <span v-if="item.country && item.country.name">{{ item.country.name }}</span>
        <span v-else class="ui-muted">не указано</span>
      </template>

      <!-- соцсети: чипы в строку вместо вертикального списка ссылок -->
      <template #cell(socials)="{item}">
        <div class="ui-chips">
          <a v-for="account in visibleSocials(item)" :key="'account-' + account.id"
             class="ui-social" target="_blank" rel="noopener"
             :href="accountLink(account)">
            <i class="fa" :class="providerIcon(account.provider)"></i>
            {{ account.provider }}
          </a>
          <span v-if="!visibleSocials(item).length" class="ui-muted">—</span>
        </div>
      </template>

      <template #cell(role)="{item}">
        <span class="tf-badge" :class="roleBadgeClass(item.role_in_team)">
          {{ roleTitle(item.role_in_team) }}
        </span>
      </template>

      <template #cell(actions)="{item}">
        <div class="ui-members__actions">
          <b-dropdown right no-caret variant="link" toggle-class="ui-act" menu-class="ui-menu">
            <template #button-content>
              <i class="fa fa-user-circle-o" v-b-tooltip.hover.top="'Изменить роль'"></i>
            </template>
            <b-dropdown-item v-for="role in assignableRoles" :key="'role-' + role.id"
                             @click="assignRole(item, role.id)">
              {{ role.label }}
            </b-dropdown-item>
          </b-dropdown>
          <button type="button" class="ui-act ui-act--danger" @click="openKickMemberModal(item)"
                  v-b-tooltip.hover.top="'Удалить из команды'">
            <i class="fa fa-user-times"></i>
          </button>
        </div>
      </template>
    </b-table>

    <b-modal centered :id="kickModalId" title="Удаление игрока из команды"
             v-model="isShowKickMemberModal" hide-footer>
      <p>Вы действительно хотите удалить игрока
        <strong>{{ kickMemberTarget && kickMemberTarget.username }}</strong>
        из команды?
      </p>
      <div v-if="kickMemberError" class="tf-err">{{ kickMemberError }}</div>
      <div class="d-flex justify-content-end mt-3">
        <button type="button" class="tf-btn-ghost mr-2" :disabled="isKickingMember"
                @click="isShowKickMemberModal = false">Отмена
        </button>
        <button type="button" class="tf-btn-primary ui-btn-danger-solid" :disabled="isKickingMember"
                @click="confirmKickMember">
          <i class="fa" :class="isKickingMember ? 'fa-spinner tf-spin' : 'fa-user-times'"></i>
          {{ isKickingMember ? 'Удаляем…' : 'Удалить' }}
        </button>
      </div>
    </b-modal>
  </div>
</template>

<script>
/**
 * Состав команды для карточки команды.
 *
 * Раньше было два почти одинаковых компонента — PubgPlayerTable и MlbbPlayerTable,
 * они отличались только слагом дисциплины и названием игрового id. Теперь это
 * один компонент: дисциплина приходит пропом gameSlug.
 */
import {UsersAdapterMixin} from "@/mixins/Users/UsersAdapterMixin";
import {TeamsAdapterMixin} from "@/mixins/Teams/TeamsAdapterMixin";
import {ACCOUNTS} from '@/constants/SocialAccounts/providers';
import {getTeamMemberRoleTitle} from '@/constants/TeamMember/roles';
import api from "@/api/api";

/** id роли -> подпись действия. Значения оставлены как были в старых таблицах. */
const ASSIGNABLE_ROLES = [
  {id: 2, label: 'Назначить игроком'},
  {id: 3, label: 'Назначить менеджером'},
  {id: 1, label: 'Назначить владельцем'},
];

const PROVIDER_ICONS = {
  discord: 'fa-comments',
  telegram: 'fa-paper-plane',
  email: 'fa-envelope-o',
  google: 'fa-google',
};

const GAMES = {
  'pubg-mobile': {profileLabel: 'PUBG', idField: 'pubg_id', idLabel: 'PUBG ID'},
  mlbb: {profileLabel: 'MLBB', idField: 'mlbb_id', idLabel: 'MLBB ID'},
};

export default {
  name: 'TeamMembersTable',
  mixins: [UsersAdapterMixin, TeamsAdapterMixin],
  props: {
    teamMembers: {
      type: Array,
      required: true
    },
    /** slug дисциплины команды: pubg-mobile или mlbb */
    gameSlug: {
      type: String,
      default: null
    },
  },

  data() {
    return {
      assignableRoles: ASSIGNABLE_ROLES,
      isShowKickMemberModal: false,
      isKickingMember: false,
      kickMemberTarget: null,
      kickMemberError: null,
    }
  },

  computed: {
    game() {
      return GAMES[this.gameSlug] || {profileLabel: 'Игровой ник', idField: null, idLabel: 'Игровой ID'};
    },

    idLabel() {
      return this.game.idLabel;
    },

    fields() {
      return [
        {key: 'player', label: 'Игрок'},
        {key: 'profile', label: this.game.profileLabel},
        {key: 'status', label: 'Статус'},
        {key: 'country', label: 'Страна'},
        {key: 'socials', label: 'Соцсети'},
        {key: 'role', label: 'Роль'},
        {key: 'actions', label: ''},
      ];
    },

    kickModalId() {
      return `modal-kick-member-${this.gameSlug || 'team'}`;
    },
  },

  methods: {
    gameProfile(member) {
      const profiles = member.gameProfiles || [];
      if (!this.gameSlug) return profiles[0] || null;
      return profiles.find((profile) => profile.game && profile.game.slug === this.gameSlug) || null;
    },

    hasGameProfile(member) {
      return (member.gameProfiles || []).length > 0;
    },

    profileNick(member) {
      const profile = this.gameProfile(member);
      return (profile && profile.fields && profile.fields.nick) || '';
    },

    profileId(member) {
      const profile = this.gameProfile(member);
      if (!profile || !profile.fields) return '';
      return (this.game.idField && profile.fields[this.game.idField]) || '';
    },

    isActivated(member) {
      return (member.verified_social_accounts_count || 0) > 0;
    },

    isBanned(member) {
      return Boolean((member.currentBans || []).length);
    },

    /** Имя socialAccounts занято computed-свойством в UsersAdapterMixin. */
    visibleSocials(member) {
      return (member.socialAccounts || []).filter((account) => !['phone', 'whatsapp'].includes(account.provider));
    },

    accountLink(account) {
      const base = ACCOUNTS[account.provider] || '';
      if (account.provider === 'telegram') return `${base}${account.data && account.data.username}`;
      if (account.provider === 'email') return `${base}${account.data && account.data.email}`;
      return `${base}${account.provider_id}`;
    },

    providerIcon(provider) {
      return PROVIDER_ICONS[provider] || 'fa-link';
    },

    roleTitle(role) {
      return getTeamMemberRoleTitle(role);
    },

    roleBadgeClass(role) {
      const code = role && (role.code || String(role.name || '').split('.').pop());
      if (code === 'owner') return 'tf-badge--warn';
      if (code === 'manager') return 'tf-badge--primary';
      return '';
    },

    assignRole(member, roleId) {
      return this.changeMemberRole({teamId: this.$route.params.id, userId: member.id, roleId});
    },

    copyValue(value, label) {
      if (value === null || value === undefined || value === '') return;
      const done = () => this.$bvToast.toast(`${label} скопирован`, {
        title: 'Готово', variant: 'success', solid: true,
      });
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(String(value)).then(done).catch(() => {
        });
      }
    },

    openKickMemberModal(member) {
      this.kickMemberTarget = member;
      this.kickMemberError = null;
      this.isShowKickMemberModal = true;
    },

    async confirmKickMember() {
      if (!this.kickMemberTarget) return;
      this.isKickingMember = true;
      this.kickMemberError = null;
      try {
        await api.teams.deleteTeamMember(this.$route.params.id, this.kickMemberTarget.id);
        this.isShowKickMemberModal = false;
        this.$bvToast.toast('Игрок успешно удалён из команды', {
          title: 'Успех',
          variant: 'success',
          solid: true
        });
        await this.showTeams(this.$route.params.id);
      } catch (error) {
        this.kickMemberError = (error && error.response && error.response.data && error.response.data.message)
            || 'Не удалось удалить игрока';
      } finally {
        this.isKickingMember = false;
      }
    },
  }
}
</script>

<style scoped lang="scss">
@import "~@/assets/scss/tournament-form-tokens";
@import "~@/assets/scss/tournament-form-buttons";

/* кнопки дизайн-системы: и в компоненте, и в модалке, которую b-modal уносит в body */
@include tf-buttons;

.ui-members {
  @include tf-tokens-light;

  min-width: 0;
}

body.dark-only .ui-members {
  @include tf-tokens-dark;
}

/* ---------- таблица ---------- */
.ui-members__table ::v-deep {
  margin: 0;
  color: var(--tf-text);

  @media (min-width: 768px) {
    table { min-width: 880px; }
  }

  .table-responsive { margin-bottom: 0; }

  thead th {
    background: var(--tf-bg);
    border: 0 !important;
    border-bottom: 1px solid var(--tf-border) !important;
    padding: 11px 16px;
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: .05em;
    color: var(--tf-muted);
    white-space: nowrap;
  }

  tbody td {
    border: 0 !important;
    border-bottom: 1px solid var(--tf-border) !important;
    padding: 10px 16px;
    font-size: 13px;
    vertical-align: middle;
  }

  tbody tr:last-child td { border-bottom: 0 !important; }

  tbody tr:hover td { background: var(--tf-soft); }

  /* ниже md bootstrap-vue разворачивает строку в список «label — значение» */
  @media (max-width: 767px) {
    tbody tr {
      display: block;
      border-bottom: 1px solid var(--tf-border);
    }

    tbody td { border-bottom: 0 !important; }
  }
}

/* ---------- игрок ---------- */
.ui-members__nick {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 0;
  border: 0;
  background: none;
  color: var(--tf-text);
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;

  i { font-size: 10px; opacity: 0; transition: opacity .14s ease; }

  &:hover {
    color: var(--tf-primary);

    i { opacity: 1; }
  }
}

.ui-members__game-nick { font-weight: 500; }

.ui-copy {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  margin-top: 2px;
  padding: 0;
  border: 0;
  background: none;
  color: var(--tf-muted);
  font-size: 11.5px;
  cursor: pointer;

  i { opacity: 0; font-size: 10px; transition: opacity .14s ease; }

  &:hover {
    color: var(--tf-primary);

    i { opacity: 1; }
  }
}

.ui-muted { color: var(--tf-muted); font-size: 12.5px; }

/* ---------- статусы и соцсети ---------- */
.ui-chips {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px;
}

.tf-badge {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  height: 21px;
  padding: 0 9px;
  border-radius: 999px;
  background: var(--tf-soft-2);
  color: var(--tf-muted);
  font-size: 11px;
  font-weight: 600;
  white-space: nowrap;

  &--ok { background: var(--tf-ok-soft); color: var(--tf-ok); }

  &--warn { background: var(--tf-warn-soft); color: #b57512; }

  &--danger { background: var(--tf-danger-soft); color: var(--tf-danger); }

  &--primary { background: var(--tf-primary-soft); color: var(--tf-primary); }
}

.ui-social {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  height: 21px;
  padding: 0 9px;
  border: 1px solid var(--tf-border);
  border-radius: 999px;
  color: var(--tf-muted);
  font-size: 11px;
  font-weight: 500;

  i { font-size: 10px; }

  &:hover {
    border-color: var(--tf-primary);
    background: var(--tf-primary-soft);
    color: var(--tf-primary);
    text-decoration: none;
  }
}

/* ---------- действия ---------- */
.ui-members__actions {
  display: flex;
  align-items: center;
  gap: 6px;
}

.ui-members ::v-deep .ui-act,
.ui-act {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  padding: 0;
  border: 1px solid var(--tf-border);
  border-radius: 9px;
  background: var(--tf-bg);
  color: var(--tf-muted);
  font-size: 12px;
  cursor: pointer;

  &:hover, &:focus {
    border-color: var(--tf-primary);
    background: var(--tf-primary-soft);
    color: var(--tf-primary);
    box-shadow: none;
  }

  &--danger:hover {
    border-color: var(--tf-danger);
    background: var(--tf-danger-soft);
    color: var(--tf-danger);
  }
}

.ui-members ::v-deep .ui-menu {
  min-width: 210px;
  padding: 6px;
  border: 1px solid var(--tf-border);
  border-radius: 12px;
  box-shadow: var(--tf-shadow-lg);

  .dropdown-item {
    border-radius: 9px;
    padding: 8px 10px;
    font-size: 13px;
    color: var(--tf-text);

    &:hover { background: var(--tf-soft); }
  }
}
</style>
